import { Brain, Radio, CalendarCheck2, ArrowRight } from "lucide-react";
import { Container, Section, Eyebrow, Reveal } from "@/components/kit";

const stages = [
  {
    icon: Brain,
    step: "Stage 01",
    title: "ICP Intelligence",
    desc: "Deep research maps your total addressable market, scores accounts, and enriches every contact with firmographic and intent data.",
    points: ["TAM & account scoring", "Signal & intent enrichment", "Messaging research"],
  },
  {
    icon: Radio,
    step: "Stage 02",
    title: "Multi-Channel Execution",
    desc: "Email, LinkedIn and calling systems run in orchestrated sequences — tested, warmed and optimized for reply and book rates.",
    points: ["Warmed sending infra", "A/B tested sequences", "Human + AI SDR layer"],
  },
  {
    icon: CalendarCheck2,
    step: "Stage 03",
    title: "Qualified Booking",
    desc: "Interested prospects are vetted against your parameters, then booked straight onto your calendar — only qualified ones count.",
    points: ["Pre-call qualification", "Calendar hand-off", "Show-rate management"],
  },
];

export const HowItWorks = () => (
  <Section data-testid="how-it-works" className="bg-navy-800/30 border-y border-white/10">
    <Container>
      <div className="max-w-3xl">
        <Eyebrow>How It Works</Eyebrow>
        <h2 className="mt-6 font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white">
          A three-stage outbound engine
        </h2>
        <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed">
          Research becomes execution becomes qualified pipeline. Each stage feeds the next.
        </p>
      </div>

      <div className="relative mt-16 grid gap-8 lg:grid-cols-3">
        <div className="pointer-events-none absolute top-[64px] left-0 right-0 hidden lg:block">
          <div className="mx-auto h-px w-[78%] bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
        </div>

        {stages.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.12} className="relative">
            <div className="group relative h-full rounded-2xl border border-white/10 bg-navy-900 p-8 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-2 hover:border-gold/50 hover:shadow-[0_0_40px_rgba(255,215,0,0.12)]">
              <div className="flex items-center justify-between">
                <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-2xl bg-gold text-navy-900">
                  <s.icon className="h-7 w-7" />
                </div>
                <span className="font-mono-jb text-xs tracking-[0.2em] uppercase text-gold/70">
                  {s.step}
                </span>
              </div>
              <h3 className="mt-6 font-display text-2xl font-semibold text-white">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/55">{s.desc}</p>
              <ul className="mt-6 space-y-2.5">
                {s.points.map((p) => (
                  <li key={p} className="flex items-center gap-2.5 text-sm text-white/70">
                    <ArrowRight className="h-3.5 w-3.5 text-gold" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
    </Container>
  </Section>
);
